import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { captureAffiliateAttribution, getToken, getPublicTrackingConfig, getTrackingEvents, sendTrackingPageView, updateTrackingConsent } from "@/lib/api";
import { ATTRIBUTION_KEY, buildTrackingDocument, captureUtm, getTrackingConsent, getTrackingContext, isTrackingExcludedPath, markTrackingEventSeen, safeTrackingEvent, safeTrackingPath, setTrackingConsent, trackingConsentChosen, TRACKING_SANDBOX } from "@/lib/tracking";

export default function TrackingRuntime() {
  const location = useLocation();
  const { user } = useAuth();
  const [consent, setConsent] = useState(() => getTrackingConsent());
  const [chosen, setChosen] = useState(() => trackingConsentChosen());
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const frame = useRef(null);
  const pending = useRef([]);
  const readyRef = useRef(false);
  const activeRef = useRef(false);
  const configRef = useRef(null);
  const pageSources = useRef(new Map());
  activeRef.current = consent && config?.enabled === true;
  const document = useMemo(() => config?.enabled ? buildTrackingDocument(config) : "", [config]);
  const path = safeTrackingPath(location.pathname);
  useEffect(() => {
    // First-touch campaign attribution is bounded and never includes a URL
    // token. Affiliate code capture remains a separate server-owned flow.
    try {
      const values = captureUtm(window.location.search);
      if (Object.keys(values).length && !Object.keys(getTrackingContext().attribution).length) {
        window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ ...values, captured_at: Date.now() }));
      }
    } catch { /* private browsing / blocked storage: continue without attribution */ }
    captureAffiliateAttribution().catch(() => {});
  }, [location.search]);
  const send = useCallback((raw) => {
    const event = safeTrackingEvent(raw);
    if (!event || !activeRef.current) return;
    if (!readyRef.current || !frame.current?.contentWindow) {
      if (pending.current.length < 200 && !pending.current.some((item) => item.event_id === event.event_id)) pending.current.push(event);
      return;
    }
    if (!markTrackingEventSeen(event.event_id)) return;
    frame.current.contentWindow.postMessage({ type: "mastofa:tracking", event }, "*");
  }, []);
  useEffect(() => {
    let cancelled = false;
    const refresh = () => getPublicTrackingConfig().then((value) => {
      if (cancelled) return;
      const current = configRef.current;
      if (current && current.revision === value.revision && current.enabled === value.enabled) return;
      readyRef.current = false;
      setReady(false);
      configRef.current = value;
      setConfig(value);
    }).catch(() => {});
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (user?.id && getToken()) updateTrackingConsent(consent).catch(() => {});
  }, [user?.id, consent]);
  useEffect(() => {
    const sync = (event) => {
      if (!event.key || event.key === "mastofa_tracking_consent") {
        setConsent(getTrackingConsent());
        setChosen(trackingConsentChosen());
      }
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  useEffect(() => {
    const receive = (event) => {
      // Opaque-origin iframe sends origin null. Trust only its exact Window.
      if (activeRef.current && event.source === frame.current?.contentWindow && event.data?.type === "mastofa:tracking-ready") { readyRef.current = true; setReady(true); }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);
  useEffect(() => { if (!consent || !config?.enabled) { pending.current = []; readyRef.current = false; setReady(false); } }, [consent, config]);
  useEffect(() => {
    if (!ready || !consent) return;
    const queued = pending.current.splice(0);
    queued.forEach(send);
  }, [ready, consent, send]);
  useEffect(() => {
    if (!consent || !config?.enabled) return;
    // Operator pages are not reported to third-party pixels (D12).
    if (isTrackingExcludedPath(location.pathname)) return;
    let cancelled = false;
    const visit = `${location.key}:${path}`;
    if (!pageSources.current.has(visit)) pageSources.current.set(visit, `page:${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`);
    const source = pageSources.current.get(visit);
    if (pageSources.current.size > 100) pageSources.current.delete(pageSources.current.keys().next().value);
    const context = getTrackingContext();
    sendTrackingPageView({ name: "PageView", source_id: source, data: { page_path: path }, attribution: context.attribution, consent: true })
      .then((event) => { if (!cancelled) send(event); }).catch(() => {});
    if (["/", "/beli-kredit", "/video-jualan", "/shortdrama"].includes(path)) {
      sendTrackingPageView({ name: "ViewContent", source_id: source, data: { page_path: path }, attribution: context.attribution, consent: true })
        .then((event) => { if (!cancelled) send(event); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [path, location.pathname, location.key, consent, config?.enabled, send]);
  useEffect(() => {
    if (!consent || !config?.enabled || !ready || !user?.id) return;
    let cancelled = false;
    const poll = async () => {
      if (!getToken()) return;
      try {
        const result = await getTrackingEvents();
        const events = Array.isArray(result) ? result : result?.events || [];
        if (!cancelled) events.forEach(send);
      } catch { /* analytics must not affect the user's transaction */ }
    };
    poll();
    const timer = window.setInterval(poll, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [consent, config?.enabled, ready, path, user?.id, send]);
  const choose = (granted) => {
    if (!granted) { activeRef.current = false; pending.current = []; }
    setTrackingConsent(granted);
    setConsent(getTrackingConsent());
    setChosen(true);
  };
  return <>
    {consent && config?.enabled && <iframe ref={frame} title="Tracking terisolasi" sandbox={TRACKING_SANDBOX} referrerPolicy="no-referrer" srcDoc={document} style={{ display: "none" }} aria-hidden="true" />}
    {config?.enabled && !chosen && <div className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-lg rounded-xl border border-slate-700 bg-slate-950 p-4 text-sm text-white shadow-xl" role="dialog" aria-label="Pilihan analitik">
      <p>Kami menggunakan analitik untuk memahami kunjungan dan meningkatkan layanan. Pembayaran dan pembuatan video tetap dapat digunakan tanpa analitik.</p>
      <div className="mt-3 flex gap-3"><button className="rounded bg-violet-600 px-4 py-2" onClick={() => choose(true)}>Izinkan analitik</button><button className="rounded border border-slate-600 px-4 py-2" onClick={() => choose(false)}>Tanpa analitik</button></div>
    </div>}
    {config?.enabled && chosen && <button type="button" onClick={() => { setChosen(false); }} className="fixed bottom-2 right-3 z-40 rounded border border-slate-500 bg-slate-950 px-2 py-1 text-xs text-white" aria-label="Ubah pilihan analitik">Pilihan analitik</button>}
  </>;
}
